/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/layout/Layout';
import { useNavigate } from 'react-router-dom';
import * as shaApi from '../services/sha.api';
import { fmtDate, fmtDateTime } from '../utils/helpers';

const BTN  = 'bg-[#039089] hover:bg-teal-700 text-white font-semibold py-2.5 px-5 rounded-xl transition-all text-sm shadow-md disabled:opacity-50 disabled:cursor-not-allowed';
const BTN2 = 'bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2.5 px-5 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all text-sm disabled:opacity-50';

const SCENARIO_META = [
  { id:1, label:'S1 — SHIF IP Per Diem',         fund:'SHIF', access:'IP', flow:'visit → diagnoses → preview → discharge' },
  { id:2, label:'S2 — SHIF IP FFS Normal',        fund:'SHIF', access:'IP', flow:'visit + preauth (auto) → bill → discharge' },
  { id:3, label:'S3 — SHIF IP FFS Elective',      fund:'SHIF', access:'IP', flow:'pre-visit auth → preauth (doctor) → day-of-visit → bill → discharge' },
  { id:4, label:'S4 — SHIF OP FFS Elective',      fund:'SHIF', access:'OP', flow:'visit + preauth (doctor) → bill → submit' },
  { id:5, label:'S5 — SHIF OP FFS Normal',        fund:'SHIF', access:'OP', flow:'visit + preauth (auto) → bill → submit' },
  { id:6, label:'S6 — UHC OP Capitation',         fund:'UHC',  access:'OP', flow:'visit (CAPITATION) → line + diagnoses → preview → submit' },
  { id:7, label:'S7 — ECCIF Emergency (ID)',       fund:'ECCIF',access:'EM', flow:'emergency claim → doctor consent → protocols → diagnoses → submit' },
  { id:8, label:'S8 — ECCIF Emergency (Unknown)', fund:'ECCIF',access:'EM', flow:'emergency claim (no ID) → doctor consent → protocols → identify? → submit' },
];

const STATUS_CSS: Record<string,string> = {
  INITIATED:'bg-slate-100 text-slate-600', CREATING_VISIT:'bg-yellow-100 text-yellow-700',
  PRE_VISIT_AUTHORIZED:'bg-amber-100 text-amber-700', CREATING_PREAUTH:'bg-orange-100 text-orange-700',
  PENDING_DOCTOR_APPROVAL:'bg-red-100 text-red-700', PREAUTH_FINALISED:'bg-teal-100 text-teal-700',
  CLAIM_CREATED:'bg-blue-100 text-[#02357A]', BILLING_ADDED:'bg-indigo-100 text-indigo-700',
  PREVIEWED:'bg-purple-100 text-purple-700', DISCHARGED:'bg-emerald-100 text-emerald-700',
  SUBMITTED:'bg-green-100 text-green-800 font-bold', PATIENT_IDENTIFIED:'bg-cyan-100 text-cyan-700',
  CLOSED:'bg-slate-100 text-slate-500', PAID:'bg-green-200 text-green-900 font-black',
  // Statuses the DHA webhooks (sha-webhook.controller.ts) can actually set once a claim
  // leaves the interactive flow — these need to visually stand out as needing action,
  // not blend in with the routine in-progress statuses above.
  CLAIM_REJECTED:'bg-red-200 text-red-900 font-black', PREAUTH_REJECTED:'bg-red-200 text-red-900 font-black',
  DOCTOR_CONSENT_REJECTED:'bg-red-200 text-red-900 font-black', AUTH_FAILED:'bg-red-200 text-red-900 font-black',
  PARTIALLY_PAID:'bg-amber-200 text-amber-900 font-black',
};

// Statuses that mean "DHA bounced this — it needs a fix + resubmit", vs. statuses that
// are just still moving through the normal flow.
const REJECTED_STATUSES = ['CLAIM_REJECTED','PREAUTH_REJECTED','DOCTOR_CONSENT_REJECTED','AUTH_FAILED'];

export default function SHAPage() {
  const nav = useNavigate();
  const [tab, setTab] = useState<'dashboard'|'eligibility'|'claims'|'audit'>('dashboard');
  const [claims, setClaims] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({});
  const [audit, setAudit] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  // Eligibility
  const ID_MAP:Record<string,string>={'National ID':'National ID','Passport':'Passport','Alien ID':'Alien ID','Refugee ID':'Refugee ID','Birth Certificate':'Birth Certificate','Mandate Number':'Mandate Number'};
  const [eliForm, setEliForm] = useState({ identificationNumber:'', identificationType:'National ID', serviceType:'INPATIENT' });
  const [eliResult, setEliResult] = useState<any>(null);
  const [eliLoading, setEliLoading] = useState(false);

  const notify = (m: string) => { setMsg(m); setTimeout(() => setMsg(''), 5000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([shaApi.shaListClaims({ take: 50 }), shaApi.shaGetStats()]);
      setClaims(c?.data || []); setStats(s?.data || {});
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const checkEligibility = async () => {
    if (!eliForm.identificationNumber) return notify('Enter ID number');
    setEliLoading(true);
    try {
      const r = await shaApi.shaCheckEligibility(eliForm);
      setEliResult(r?.data);
      notify('✅ Eligibility retrieved');

    } catch (e: any) {
        
        notify('❌ ' + (e?.response?.data?.message || 'Eligibility check failed'));
     }
    setEliLoading(false);
  };

  const loadAudit = async () => {
    try { const r = await shaApi.shaGetAudit({ take: 100 }); setAudit(r?.data || []); } catch {}
  };

  const [resubmitting, setResubmitting] = useState<string|null>(null);
  const doResubmit = async (c: any) => {
    if (!c.shaConsentToken) { notify('❌ No consent token on this claim — cannot resubmit'); return; }
    setResubmitting(c.id);
    try {
      await shaApi.shaResubmit(c.shaConsentToken, c.id);
      notify('✅ Claim resubmitted to DHA');
      load();
    } catch (e: any) {
      notify('❌ ' + (e?.response?.data?.message || 'Resubmit failed'));
    }
    setResubmitting(null);
  };

  const TABS = [['dashboard','📊 Dashboard'],['eligibility','🔍 Eligibility'],['claims','📋 All Claims'],['audit','📜 Audit Log']];

  return (
    <Layout title="SHA / DHA HIE" actions={
      <button onClick={() => nav('/sha/new-claim')} className={BTN}>➕ New SHA Claim</button>
    }>
      {msg && <div className={`fixed top-5 right-5 z-[99999] px-5 py-3 rounded-2xl shadow-xl text-sm font-semibold border ${msg.startsWith('✅')?'bg-emerald-50 text-emerald-800 border-emerald-200':'bg-red-50 text-red-800 border-red-200'}`}>{msg}</div>}

      <div className="flex gap-2 mb-5 flex-wrap">
        {TABS.map(([t,l]) => (
          <button key={t} onClick={() => { setTab(t as any); if(t==='audit') loadAudit(); }}
            className={`px-4 py-2 rounded-xl text-xs font-semibold border-2 transition-all ${tab===t?'bg-[#039089] text-white border-[#039089] shadow-md':'bg-white text-slate-600 border-slate-200 hover:border-[#039089] hover:text-[#039089]'}`}>{l}</button>
        ))}
      </div>

      {/* ── DASHBOARD ── */}
      {tab==='dashboard' && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-8 gap-3">
            {[['Total',stats.total||0,'from-slate-600 to-slate-700'],
              ['Claim Created',stats.created||0,'from-blue-600 to-blue-700'],
              ['Pre-Auth',stats.preauth||0,'from-orange-500 to-orange-600'],
              ['Billing Added',stats.billing||0,'from-teal-600 to-[#039089]'],
              ['Discharged',stats.discharged||0,'from-emerald-600 to-green-600'],
              ['Submitted',stats.submitted||0,'from-green-600 to-green-700'],
              ['Rejected',stats.rejected||0,'from-red-600 to-red-700'],
              ['Paid',stats.paid||0,'from-green-700 to-emerald-800'],
            ].map(([l,v,g]) => (
              <div key={String(l)} className={`bg-gradient-to-br ${g} text-white rounded-2xl p-4 shadow-md`}>
                <p className="text-xs font-semibold opacity-80 uppercase tracking-wide">{l}</p>
                <p className="text-3xl font-black mt-1">{v}</p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <h3 className="font-bold text-slate-800 mb-4">Scenarios Quick Reference</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {SCENARIO_META.map(s => (
                <div key={s.id} className="bg-slate-50 rounded-2xl p-4 border border-slate-200 flex gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#039089] text-white flex items-center justify-center font-black text-sm flex-shrink-0">S{s.id}</div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="font-bold text-sm text-slate-800">{s.label}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${s.fund==='SHIF'?'bg-blue-100 text-blue-700':s.fund==='UHC'?'bg-emerald-100 text-emerald-700':'bg-red-100 text-red-700'}`}>{s.fund}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 font-bold">{s.access}</span>
                    </div>
                    <p className="text-xs text-slate-400">{s.flow}</p>
                    <button onClick={() => nav('/sha/new-claim?scenario='+s.id)} className="mt-2 text-xs text-[#039089] hover:underline font-semibold">Start →</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent claims */}
          {claims.slice(0,5).length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                <h3 className="font-bold text-slate-700 text-sm">Recent Claims</h3>
                <button onClick={() => setTab('claims')} className="text-xs text-[#039089] hover:underline">View all →</button>
              </div>
              <div className="divide-y divide-slate-100">
                {claims.slice(0,5).map((c: any) => (
                  <div key={c.id} className="flex items-center justify-between px-5 py-3 hover:bg-slate-50 cursor-pointer" onClick={() => nav('/sha/claims/'+c.id)}>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-black bg-[#039089]/10 text-[#039089] px-2 py-0.5 rounded-full">{c.scenario?.replace('SCENARIO_','S')}</span>
                      <span className="text-sm text-slate-600 font-medium">{c.fund} · {c.serviceType}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_CSS[c.status]||'bg-slate-100 text-slate-600'}`}>{c.status}</span>
                      <span className="text-xs text-slate-400">{c.createdAt ? fmtDate(c.createdAt) : ''}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── ELIGIBILITY ── */}
      {tab==='eligibility' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <h3 className="font-bold text-slate-800 mb-4">SHA Eligibility Check</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">National ID / Passport Number *</label>
                <input value={eliForm.identificationNumber} onChange={e=>setEliForm({...eliForm,identificationNumber:e.target.value})}
                  className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#039089] transition-all placeholder:text-slate-400"
                  placeholder="e.g. 12345678" onKeyDown={e=>e.key==='Enter'&&checkEligibility()}/>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">ID Type</label>
                <select value={eliForm.identificationType} onChange={e=>setEliForm({...eliForm,identificationType:e.target.value})}
                  className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#039089] transition-all">
                  {['National ID','Passport','Alien ID','Refugee ID','Huduma Number'].map(t=><option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Service Type</label>
                <select value={eliForm.serviceType} onChange={e=>setEliForm({...eliForm,serviceType:e.target.value})}
                  className="w-full bg-white border-2 border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#039089] transition-all">
                  <option value="INPATIENT">Inpatient (IP)</option>
                  <option value="OUTPATIENT">Outpatient (OP)</option>
                </select>
              </div>
              <button onClick={checkEligibility} disabled={eliLoading} className={BTN+' w-full'}>
                {eliLoading ? 'Checking SHA...' : '🔍 Check Eligibility'}
              </button>
            </div>
          </div>

          {eliResult && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <h3 className="font-bold text-slate-800 mb-4">Result</h3>
              <div className={`p-4 rounded-2xl mb-4 text-center border-2 ${eliResult.isEligible||eliResult.eligible||(eliResult.schemes?.some((s:any)=>s?.coverage?.status==='1'))?'bg-emerald-50 border-emerald-300':'bg-red-50 border-red-300'}`}>
                <p className={`text-2xl font-black ${eliResult.isEligible||eliResult.eligible||(eliResult.schemes?.some((s:any)=>s?.coverage?.status==='1'))?'text-emerald-600':'text-red-600'}`}>
                  {eliResult.isEligible||eliResult.eligible||(eliResult.schemes?.some((s:any)=>s?.coverage?.status==='1'))?'✅ ELIGIBLE':'❌ NOT ELIGIBLE'}
                </p>
                {eliResult.memberCrNumber||eliResult.shaBeneficiaryCrId && <p className="text-xs text-slate-500 font-mono mt-1">CR ID: {eliResult.memberCrNumber||eliResult.shaBeneficiaryCrId}</p>}
              </div>

              {eliResult.isEligible && (
                <button onClick={() => {
                  const blob = JSON.stringify(eliResult.schemes || []).toUpperCase();
                  const fund = blob.includes('ECCIF') ? 'ECCIF' : (blob.includes('UHC') ? 'UHC' : 'SHIF');
                  nav(`/sha/new-claim?crId=${eliResult.memberCrNumber||eliResult.shaBeneficiaryCrId||''}&idNo=${eliForm.identificationNumber}&idType=${eliForm.identificationType}&serviceType=${eliForm.serviceType}&fund=${fund}`);
                }}
                  className={BTN+' w-full mb-4'}>➕ Start SHA Claim for This Patient</button>
              )}

              {eliResult.schemes?.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Active Schemes</p>
                  {eliResult.schemes.map((s: any, i: number) => (
                    <div key={i} className="bg-slate-50 rounded-xl p-3 mb-2">
                      <p className="font-semibold text-sm">{s.scheme_name||s.name}</p>
                      <p className="text-xs text-slate-400">{s.member_type} · Exp: {s.expiry_date}</p>
                    </div>
                  ))}
                </div>
              )}
              {eliResult.interventions?.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Covered Interventions ({eliResult.interventions.length})</p>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {eliResult.interventions.map((iv: any, i: number) => (
                      <div key={i} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-3 py-1.5">
                        <span className="font-mono text-[#039089] font-bold">{iv.intervention_code||iv.code}</span>
                        <span className="text-slate-500 truncate ml-2">{iv.intervention_name||iv.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── CLAIMS LIST ── */}
      {tab==='claims' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-bold text-slate-800">All SHA Claims ({claims.length})</h3>
            <div className="flex gap-2">
              <button onClick={load} className={BTN2+' text-xs'}>🔄 Refresh</button>
              <button onClick={() => nav('/sha/new-claim')} className={BTN+' text-xs'}>➕ New Claim</button>
            </div>
          </div>
          {loading ? <div className="p-12 text-center"><div className="w-8 h-8 border-4 border-[#039089]/20 border-t-[#039089] rounded-full animate-spin mx-auto"/></div>
          : claims.length===0 ? <div className="p-12 text-center text-slate-400"><p className="text-5xl mb-3">📋</p><p>No SHA claims yet</p></div>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50 border-b border-slate-100">
                  {['Scenario','Fund','Type','Payment','Status','Patient','Date',''].map(h=>(
                    <th key={h} className="px-4 py-2.5 text-xs font-bold text-slate-500 uppercase text-left whitespace-nowrap">{h}</th>
                  ))}
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {claims.map((c: any) => (
                    <tr key={c.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3"><span className="text-xs font-black bg-[#039089]/10 text-[#039089] px-2 py-0.5 rounded-full">{c.scenario?.replace('SCENARIO_','S')}</span></td>
                      <td className="px-4 py-3 font-semibold text-xs">{c.fund}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{c.serviceType}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{c.paymentMechanism}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_CSS[c.status]||'bg-slate-100 text-slate-600'}`}>{c.status}</span>
                        {REJECTED_STATUSES.includes(c.status) && c.errorMessage && (
                          <p className="text-xs text-red-500 mt-1 max-w-[220px] truncate" title={c.errorMessage}>⚠️ {c.errorMessage}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-700 truncate max-w-[180px]" title={c.patientName||c.patientId}>{c.patientName || (c.scenario==='SCENARIO_8'?'Unidentified ER':c.patientId?.slice(0,8)||'—')}</td>
                      <td className="px-4 py-3 text-xs text-slate-400">{c.createdAt?fmtDate(c.createdAt):'—'}</td>
                      <td className="px-4 py-3 flex gap-2 items-center">
                        <button onClick={() => nav(`/sha/claims/${c.id}`)} className="text-xs text-[#039089] hover:underline font-medium whitespace-nowrap">Manage →</button>
                        {c.status === 'CLAIM_REJECTED' && (
                          <button onClick={() => doResubmit(c)} disabled={resubmitting===c.id}
                            className="text-xs text-red-600 hover:underline font-semibold whitespace-nowrap disabled:opacity-50">
                            {resubmitting===c.id ? 'Resubmitting…' : '🔁 Resubmit'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── AUDIT LOG ── */}
      {tab==='audit' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-bold text-slate-800">SHA API Audit Log</h3>
            <button onClick={loadAudit} className={BTN2+' text-xs'}>🔄 Reload</button>
          </div>
          {audit.length===0 ? <div className="p-12 text-center text-slate-400"><p className="text-5xl mb-3">📜</p><p>No audit logs yet</p></div>
          : (
            <div className="overflow-x-auto max-h-[65vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0"><tr className="bg-slate-50 border-b border-slate-100">
                  {['Time','Action','Endpoint','Status','Duration','Error'].map(h=>(
                    <th key={h} className="px-4 py-2.5 text-xs font-bold text-slate-500 uppercase text-left">{h}</th>
                  ))}
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {audit.map((a: any, i: number) => (
                    <tr key={i} className={`hover:bg-slate-50 ${!a.success?'bg-red-50/50':''}`}>
                      <td className="px-4 py-2 text-xs text-slate-400 whitespace-nowrap">{a.createdAt?fmtDateTime(a.createdAt):'—'}</td>
                      <td className="px-4 py-2"><span className="text-xs bg-teal-50 text-[#039089] px-2 py-0.5 rounded-lg font-semibold">{a.action}</span></td>
                      <td className="px-4 py-2 text-xs font-mono text-slate-500 max-w-[200px] truncate">{a.endpoint}</td>
                      <td className="px-4 py-2"><span className={`text-xs px-2 py-0.5 rounded-full font-bold ${a.success?'bg-green-100 text-green-700':'bg-red-100 text-red-700'}`}>{a.statusCode}</span></td>
                      <td className="px-4 py-2 text-xs text-slate-400">{a.durationMs?a.durationMs+'ms':'—'}</td>
                      <td className="px-4 py-2 text-xs text-red-500 max-w-[180px] truncate">{a.errorMessage||''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}
